import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { GiTempleGate } from "react-icons/gi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = () => {
    setLoading(true);
    api
      .get("/products/admin/all")
      .then((res) => setProducts(res.data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete product "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      setProducts(products.filter((p) => p._id !== id));
    } catch (err) {
      toast.error("Failed to delete product");
    }
  };

  const toggleStock = async (product) => {
    try {
      const { data } = await api.put(`/products/${product._id}`, { inStock: !product.inStock });
      setProducts(products.map((p) => (p._id === product._id ? data : p)));
      toast.success(data.inStock ? "Marked In Stock" : "Marked Out of Stock");
    } catch (err) {
      toast.error("Failed to update stock status");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl text-maroon-800">Marketplace Products</h1>
        <Link to="/admin/products/new" className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors">
          <FiPlus /> New Product
        </Link>
      </div>

      {products.length === 0 ? (
        <EmptyState title="No products yet" subtitle="Click 'New Product' to add your first marketplace item." />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-5 py-3 font-medium">Image</th>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Price</th>
                <th className="px-5 py-3 font-medium">Stock</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p._id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3">
                    <div className="w-12 h-12 rounded-lg bg-gold-50 overflow-hidden flex items-center justify-center">
                      {p.images && p.images[0] ? (
                        <img src={imgUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <GiTempleGate className="text-saffron-400" size={22} />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-medium text-maroon-800 max-w-xs truncate">{p.name}</td>
                  <td className="px-5 py-3 text-gray-600">{p.category}</td>
                  <td className="px-5 py-3 text-gray-600">
                    {p.discountPrice > 0 ? (
                      <><span className="font-semibold">₹{p.discountPrice}</span> <span className="line-through text-gray-400">₹{p.price}</span></>
                    ) : (
                      <span className="font-semibold">₹{p.price}</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => toggleStock(p)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${p.inStock ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}
                    >
                      {p.inStock ? "In Stock" : "Out of Stock"}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/products/edit/${p._id}`} className="p-2 rounded-lg hover:bg-gray-100 text-saffron-600">
                        <FiEdit2 />
                      </Link>
                      <button onClick={() => handleDelete(p._id, p.name)} className="p-2 rounded-lg hover:bg-gray-100 text-red-500">
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
